import { twMerge } from '@shared/themes';
import { ROUTES } from '@shared/utils';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import type { ComponentProps } from 'react';
import type { LotteryName } from '../../../lottery-purchases/types';

type ViewLatestProps = ComponentProps<'div'> & {
  lotteryName: LotteryName;
};

/**
 * ViewArchive component displays a label and a link to view the latest lottery results.
 *
 * It uses translations for the label and link text, and constructs the latest URL
 * based on the current pathname and the translated latest path.
 * Expected to be used in the ResultsHeader component only on Latest Results page.
 *
 * @param {LotteryName} lotteryName - The name of the lottery.
 * @param {ViewLatestProps} props - The props for the ViewLatest component.
 * @param {string} [props.className] - Optional additional class names for styling.
 * @returns {JSX.Element} The rendered ViewLatest component.
 */

function ViewLatest({ lotteryName, className, ...props }: ViewLatestProps) {
  const t = useTranslations('lotteries.results');

  const { RESULTS } = ROUTES.LOTTO;

  const classes = {
    container: twMerge('mx-auto flex gap-x-1 xl:flex-col xl:items-end', className),
    label: 'paragraph-explanatory xl:paragraph',
    link: 'link-label-explanatory underline xl:link-label',
  };

  return (
    <div className={classes.container} {...props}>
      <p className={classes.label}>{t('more_results_label')}</p>
      <Link href={RESULTS[lotteryName.toUpperCase() as keyof typeof RESULTS] ?? ''} className={classes.link}>
        {t('view_latest')}
      </Link>
    </div>
  );
}

export { ViewLatest };
